import { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { Button } from '@tt/design-system';
import { cropImageToDataUrl } from '../../utils/cropImage';

type ImageCropperProps = {
  imageSrc: string;
  onConfirm: (dataUrl: string) => void;
  onCancel: () => void;
};

/**
 * Wrapper de react-easy-crop com estética cerimonial.
 * Aspect 1:1 fixo, overlay dourado, controles em mono uppercase.
 */
export function ImageCropper({ imageSrc, onConfirm, onCancel }: ImageCropperProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelArea, setPixelArea] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setPixelArea(croppedAreaPixels);
  }, []);

  async function handleConfirm() {
    if (!pixelArea) return;
    setProcessing(true);
    try {
      const dataUrl = await cropImageToDataUrl(imageSrc, pixelArea);
      onConfirm(dataUrl);
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="relative h-80 w-full overflow-hidden rounded-md border border-gold/30 bg-night">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={1}
          cropShape="rect"
          showGrid={false}
          objectFit="contain"
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          style={{
            containerStyle: { backgroundColor: '#0B0E1A' },
            cropAreaStyle: {
              border: '2px solid #D4AF37',
              boxShadow: '0 0 0 9999px rgba(11,14,26,0.75)',
              borderRadius: '4px',
            },
          }}
        />
      </div>

      <div className="flex flex-col gap-3">
        <label
          htmlFor="cropper-zoom"
          className="flex items-center justify-between font-mono text-xs uppercase tracking-stamp text-champagne"
        >
          <span>Zoom</span>
          <span className="font-mono text-[10px] tabular-nums text-neutral-300">
            {zoom.toFixed(2)}×
          </span>
        </label>
        <input
          id="cropper-zoom"
          type="range"
          min={1}
          max={3}
          step={0.05}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="h-1 w-full appearance-none rounded-full bg-gold/20 accent-gold"
        />
      </div>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={processing}>
          Cancelar
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleConfirm}
          disabled={!pixelArea || processing}
        >
          {processing ? 'Aplicando…' : 'Aplicar corte'}
        </Button>
      </div>
    </div>
  );
}
